import { FC, PropsWithChildren, ReactNode } from "react";
import { useSuspenseQuery } from "@apollo/client";
import { createFileRoute, Link } from "@tanstack/react-router";
import { sortBy } from "es-toolkit";
import { KeyRound, Mail, Phone } from "lucide-react";

import { DataBlock } from "@roqua/components/DataBlock";
import { Section, SectionHeader } from "@roqua/components/sections";
import { Button } from "@roqua/components/ui/button";

import { CreateRespondentsButton } from "@/components/CreateRespondentsButton";
import { PageTitle } from "@/components/PageTitle";
import { Respondent, RespondentsForDossier } from "@/features/RespondentEditor/queries";
import { RespondentIcon } from "@/features/Respondents/RespondentIcon";
import { RespondentTypeEnum } from "@/generated/gql/graphql";
import { useTranslation } from "@/i18n/client";
import { metadataValue } from "@/models/dossier";
import { respondentTypeIndex } from "@/models/respondent";
import { useSessionContext } from "@/session";

export const Route = createFileRoute("/$organizationKey/$dossierId/dossier/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationKey, dossierId } = useSessionContext();
  const { t } = useTranslation(["RespondentEditor", "common"]);
  const { data } = useSuspenseQuery(RespondentsForDossier, {
    variables: { organizationKey, dossierId },
  });

  const respondents = sortBy(data.currentDossier.respondents, [
    (respondent) => respondentTypeIndex(respondent.type),
  ]);

  return (
    <div>
      <PageTitle title="dossier" />
      <Section variant="header">
        <SectionHeader header={t("index_header")} />
      </Section>

      <Section variant="middle">
        <div className="flex flex-col items-stretch gap-4">
          <Link
            to="/$organizationKey/$dossierId/dossier/demographics"
            params={{ organizationKey, dossierId }}
            className="bg-background flex flex-1 p-4"
          >
            <div className="flex-1">
              <div className="font-bold">{t("demographics")}</div>
              <FieldGrid>
                <Field
                  label={t("birthdate")}
                  value={data.currentDossier.demographics.birthdate?.toDateString()}
                />
                <Field
                  label={t("gender")}
                  value={t(`normGenderEnum.${data.currentDossier.demographics.gender}`)}
                />
              </FieldGrid>
            </div>
            <div>
              <Button>{t("common:edit")}</Button>
            </div>
          </Link>

          {data.organization.dossierMetadataFieldDefinitions.length > 0 && (
            <Link
              to="/$organizationKey/$dossierId/dossier/metadata"
              params={{ organizationKey, dossierId }}
              className="bg-background flex flex-1 p-4"
            >
              <div className="flex-1">
                <div className="font-bold">{t("metadata")}</div>
                <div className="space-y-2">
                  {data.organization.dossierMetadataFieldDefinitions.map((field) => (
                    <div key={field.key}>
                      <Field
                        label={field.label}
                        value={metadataValue(data.currentDossier.metadata, field.key)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Button>{t("common:edit")}</Button>
              </div>
            </Link>
          )}
        </div>
      </Section>

      <Section variant="content" className="gap-4">
        <SectionHeader header={t("respondents")} buttons={<CreateRespondentsButton />} />

        {respondents.map((respondent) => (
          <RespondentBlock key={respondent.id} respondent={respondent} />
        ))}
      </Section>
    </div>
  );
}

const RespondentBlock: FC<{ respondent: Respondent }> = ({ respondent }) => {
  const { organizationKey, dossierId } = useSessionContext();
  const { t } = useTranslation(["RespondentEditor", "common"]);

  return (
    <DataBlock
      data-testid={`respondent-${respondent.id}`}
      buttons={
        <>
          <Button variant="outline" asChild>
            <Link
              to="/$organizationKey/$dossierId/dossier/respondents/$respondentId"
              params={{
                organizationKey,
                dossierId,
                respondentId: respondent.id,
              }}
            >
              {t("common:edit")}
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link
              to="/$organizationKey/$dossierId/dossier/respondents/$respondentId/manage-login"
              params={{
                organizationKey,
                dossierId,
                respondentId: respondent.id,
              }}
            >
              Beheer login
            </Link>
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-2">
        <RespondentIcon type={respondent.type} className="mt-0.5 h-4 w-4" />
        <div className="flex gap-2">
          {respondent.type === RespondentTypeEnum.Patient &&
            (respondent.firstname || respondent.lastname) && (
              <div className="font-semibold">
                {respondent.firstname} {respondent.lastname}
              </div>
            )}
          {respondent.type !== RespondentTypeEnum.Patient && respondent.label && (
            <div className="font-semibold">{respondent.label}</div>
          )}
          <div className="text-slate-800">{t("type", { context: respondent.type })}</div>
        </div>
      </div>
      <FieldGrid>
        <Field
          label={t("email")}
          icon={<Mail className="h-4 w-4" aria-label={t("email")} />}
          value={respondent.email}
        />
        <Field
          label={t("loginToken")}
          icon={<KeyRound className="h-4 w-4" aria-label={t("loginToken")} />}
          value={respondent.loginToken}
        />
        {respondent.type === RespondentTypeEnum.Patient && (
          <Field
            label={t("phoneCell")}
            value={respondent.phoneCell}
            icon={<Phone className="h-4 w-4" aria-label={t("phoneCell")} />}
          />
        )}
      </FieldGrid>
    </DataBlock>
  );
};

const FieldGrid: FC<PropsWithChildren> = ({ children }) => (
  <div className="grid grid-cols-[min-content_1fr] items-center gap-x-2">{children}</div>
);

const Field: FC<{ label: string; icon?: ReactNode; value: ReactNode }> = ({
  label,
  icon,
  value,
}) => {
  const { t } = useTranslation("RespondentEditor");

  return (
    <>
      <div>{icon ? icon : <span className="font-medium">{label}</span>}</div>
      <div>
        {value ? value : <span className="font-light italic text-slate-800">{t("noValue")}</span>}
      </div>
    </>
  );
};
